import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import propTypes from 'prop-types';
import { cn } from 'lib/utils';
import { useSidebarNav } from 'tailwind-components/Sidebar/sidebarNavContext';

const dropdownBase =
  '[&_.header]:relative ' +
  '[&_.header>svg]:absolute [&_.header>svg]:h-3 [&_.header>svg]:w-3 [&_.header>svg]:top-1/2 [&_.header>svg]:-translate-y-1/2 [&_.header>svg]:origin-center [&_.header>svg]:shrink-0 ' +
  '[&_.body]:overflow-y-hidden [&_.body]:max-h-0 [&_.body>a]:py-2.5 [&_.body>a]:pl-[45px] ' +
  '[&.active_.header>svg]:rotate-90 [&.active_.body]:max-h-[1000px] ';

const lightChevron =
  '[&_.header>svg]:text-grey-light hover:[&_.header>svg]:text-grey';

const darkChevron =
  '[&_.header>svg]:text-white/55 hover:[&_.header>svg]:text-white';

function Dropdown({
  mainComponent,
  children,
  forceExpanded = false,
  pathname = ''
}) {
  const [internalExpanded, setInternalExpanded] = useState(false);
  /** When route forces the section open, user can still collapse; this overrides forceExpanded. */
  const [userCollapsedWhileForced, setUserCollapsedWhileForced] =
    useState(false);
  const { isShrinked, isDarkPanel } = useSidebarNav();
  const bodyRef = useRef(null);
  const scrollToLastAfterOpenRef = useRef(false);

  useEffect(() => {
    setInternalExpanded(false);
    setUserCollapsedWhileForced(false);
  }, [pathname]);

  const expanded = forceExpanded ? !userCollapsedWhileForced : internalExpanded;

  useLayoutEffect(() => {
    if (!expanded || !scrollToLastAfterOpenRef.current) {
      return;
    }
    scrollToLastAfterOpenRef.current = false;
    const last = bodyRef.current?.lastElementChild;
    if (!last) {
      return;
    }
    requestAnimationFrame(() => {
      last.scrollIntoView({ block: 'end', behavior: 'smooth' });
    });
  }, [expanded]);

  const toggleHeader = () => {
    if (forceExpanded) {
      setUserCollapsedWhileForced(wasCollapsed => {
        if (wasCollapsed) {
          scrollToLastAfterOpenRef.current = true;
        }
        return !wasCollapsed;
      });
      return;
    }
    setInternalExpanded(prev => {
      const next = !prev;
      if (next) {
        scrollToLastAfterOpenRef.current = true;
      }
      return next;
    });
  };

  return (
    <div
      className={cn(
        dropdownBase,
        isDarkPanel ? darkChevron : lightChevron,
        expanded && 'active',
        isShrinked
          ? '[&_.header>svg]:right-[20%]'
          : '[&_.header>svg]:right-[5%]'
      )}
    >
      <div
        className='header cursor-pointer'
        role='button'
        tabIndex={0}
        onClick={toggleHeader}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleHeader();
          }
        }}
      >
        {mainComponent}
        <ChevronRight />
      </div>
      <div ref={bodyRef} className='body'>
        {children}
      </div>
    </div>
  );
}

Dropdown.propTypes = {
  mainComponent: propTypes.element,
  children: propTypes.node,
  forceExpanded: propTypes.bool,
  pathname: propTypes.string
};

export default Dropdown;
